import apiClient from './apiClient';

const B = '/wms/seasonal-storage';

// ── Location Setup ──
export const createLocation = async (data) => (await apiClient.post(`${B}/admin/locations`, data)).data;
export const getLocations = async (level = null) =>
  (await apiClient.get(`${B}/admin/locations`, { params: level ? { level } : {} })).data;
export const deleteLocation = async (id) => (await apiClient.delete(`${B}/admin/locations/${id}`)).data;
export const getHalls = async () => (await apiClient.get(`${B}/admin/halls`)).data;

// ── Bin Lock / Release ──
export const getBinLocks = async () => (await apiClient.get(`${B}/bin-locks`)).data;
export const setBinLock = async (payload) => (await apiClient.post(`${B}/bin-locks/set`, payload)).data;
export const getBinLocksSummary = async () => (await apiClient.get(`${B}/bin-locks/summary`)).data;

// ── Put Away ──
export const scanBox = async (boxCode) => (await apiClient.get(`${B}/put-away/scan`, { params: { box_code: boxCode } })).data;
export const putAway = async (payload) => (await apiClient.post(`${B}/put-away`, payload)).data;
export const markBinFull = async (locationId) => (await apiClient.post(`${B}/put-away/mark-bin-full`, { location_id: locationId })).data;
export const resolveLocation = async (code, boxCode) =>
  (await apiClient.get(`${B}/put-away/resolve-location`, { params: { code, box_code: boxCode || undefined } })).data;

// ── Create Pick Slip ──
export const getPickSlipCustomers = async () => (await apiClient.get(`${B}/pick-slip/customers`)).data;
export const getPickSlipSlugs = async (customer, place) => (await apiClient.get(`${B}/pick-slip/slugs`, { params: { customer, place } })).data;
export const getPickSlipBoxes = async (customer, slug, place) => (await apiClient.get(`${B}/pick-slip/boxes`, { params: { customer, slug, place } })).data;
export const getPickSlipSlugTree = async (customer, place) => (await apiClient.get(`${B}/pick-slip/slug-tree`, { params: { customer, place } })).data;
export const createPickSlip = async (payload) => (await apiClient.post(`${B}/pick-slip/create`, payload)).data;
// Excel upload → create pick slips: preview (parse+validate+recommend) then confirm.
export const previewPickSlipExcel = async (file) => {
  const form = new FormData();
  form.append('file', file);
  return (await apiClient.post(`${B}/pick-slip/upload-excel-preview`, form, { headers: { 'Content-Type': undefined } })).data;
};
export const confirmPickSlipExcel = async (payload) => (await apiClient.post(`${B}/pick-slip/upload-excel-confirm`, payload)).data;
// Active transporters (transport_master) for the DC-tag transport dropdown.
export const getActiveTransports = async () => (await apiClient.get('/transport/active')).data;

// Weight-based transporter recommendation for the SELECTED seasonal boxes
// (same engine the regular FG pick slips use).
export const recommendTransport = async (payload) =>
  (await apiClient.post(`${B}/pick-slip/recommend-transport`, payload)).data;

// ── Available Pickers ──
export const getPickers = async () => (await apiClient.get(`${B}/available-pickers`)).data;
export const setPickers = async (staffIds) => (await apiClient.post(`${B}/available-pickers`, { staff_ids: staffIds })).data;

// ── Box Pick Slips (list + (re)assign picker) ──
export const getBoxPickSlips = async (status, q) =>
  (await apiClient.get(`${B}/pick-slips`, { params: { status: status || undefined, q: q || undefined } })).data;
export const assignBoxPicker = async (pickSlipId, staffId) =>
  (await apiClient.post(`${B}/pick-slips/${pickSlipId}/assign`, { staff_id: staffId })).data;
// Cancel some (or all) boxes from a created pick slip — cancelled boxes return to
// their original bin (stored) and are freed from the slip.
export const cancelSlipBoxes = async (pickSlipId, boxIds, reason) =>
  (await apiClient.post(`${B}/pick-slips/${pickSlipId}/cancel-boxes`, { box_ids: boxIds, reason })).data;

// ── Picking ──
export const getMySlips = async () => (await apiClient.get(`${B}/picking/my-slips`)).data;
export const getSlipDetail = async (id) => (await apiClient.get(`${B}/picking/slips/${id}`)).data;
export const scanPick = async (pickSlipId, boxNumber) => (await apiClient.post(`${B}/picking/scan`, { pick_slip_id: pickSlipId, box_number: boxNumber })).data;
// Master carton: close the current open carton (aggregates its small boxes)
export const closeCarton = async (pickSlipId) => (await apiClient.post(`${B}/picking/carton/close`, { pick_slip_id: pickSlipId })).data;
// Full URL to the master carton packing slip PDF (packing router). Fetch with Bearer token.
export const masterCartonSlipUrl = (mcId) => `${process.env.REACT_APP_BACKEND_URL}/api/wms/packing/master-carton/${mcId}/slip-pdf`;
export const getMasterCartons = async ({ q, dateFrom, dateTo, page, pageSize } = {}) =>
  (await apiClient.get(`${B}/master-cartons`, { params: {
    q: q || undefined, date_from: dateFrom || undefined, date_to: dateTo || undefined,
    page: page || 1, page_size: pageSize || 50 } })).data;
// One-time repair: finalise master cartons for past completed/dispatched slips so
// their picked boxes appear for reprint. dryRun=true only returns counts.
export const backfillMasterCartons = async (dryRun = true) =>
  (await apiClient.post(`${B}/master-cartons/backfill`, { dry_run: dryRun })).data;

// ── Stats ──
export const getStats = async () => (await apiClient.get(`${B}/stats`)).data;

// ── Location Stock Report ──
export const getStockReport = async (q, customer) =>
  (await apiClient.get(`${B}/stock-report`, { params: { q: q || undefined, customer: customer || undefined } })).data;
export const cancelBox = async (boxNumber, reason) =>
  (await apiClient.post(`${B}/stock-report/cancel-box`, { box_number: boxNumber, reason: reason || null })).data;
export const relocateBox = async (boxNumber, toLocationId) =>
  (await apiClient.post(`${B}/stock-report/relocate`, { box_number: boxNumber, to_location_id: toLocationId })).data;
export const splitBox = async (boxNumber, children) =>
  (await apiClient.post(`${B}/stock-report/split-box`, { box_number: boxNumber, children })).data;
export const reslugBox = async (payload) =>
  (await apiClient.post(`${B}/stock-report/reslug-box`, payload)).data;
export const getMultiSlugSkus = async (customer) =>
  (await apiClient.get(`${B}/reports/multi-slug-skus`, { params: customer ? { customer } : {} })).data;
export const sendToPool = async (boxNumbers, reason) =>
  (await apiClient.post(`${B}/stock-report/send-to-pool`, { box_numbers: boxNumbers, reason: reason || null })).data;
export const restoreFromPool = async (boxNumbers) =>
  (await apiClient.post(`${B}/stock-report/restore-from-pool`, { box_numbers: boxNumbers })).data;
export const reservedToPool = async (boxNumbers) =>
  (await apiClient.post(`${B}/stock-report/reserved-to-pool`, { box_numbers: boxNumbers })).data;
export const getCommonPool = async (q) =>
  (await apiClient.get(`${B}/reports/common-pool`, { params: q ? { q } : {} })).data;
